/**
 * probabilitySelect — the rules behind the Probability Select node: N options,
 * each with a probability and a lock, always summing to 1.
 *
 *   - Moving one option's probability changes the OTHER UNLOCKED options to
 *     keep the total at 1, in proportion to their current values (so their
 *     ratios hold); if they are all at 0 the remainder is shared equally.
 *   - A locked option never changes. Its share caps the others: an option can
 *     go no higher than 1 minus the locked total.
 *   - A locked option cannot be moved, and nor can an option whose every
 *     other option is locked — there is nothing to take the difference from.
 *   - A new option starts at 0 (nothing else changes); a removed option's share
 *     goes to the remaining unlocked options in proportion.
 *
 * pickIndex makes the weighted draw. Everything here is pure and returns new
 * arrays; the node and GraphEvaluator own when it runs.
 *
 * Option shape: { p: number in [0, 1], locked: boolean }.
 */

export const MIN_OPTIONS = 2

/** `n` unlocked options sharing 1 equally. */
export function defaultOptions(n = MIN_OPTIONS) {
  const count = Math.max(MIN_OPTIONS, Math.floor(n))
  return Array.from({ length: count }, () => ({ p: 1 / count, locked: false }))
}

const sum = (arr) => arr.reduce((t, x) => t + x, 0)
const clamp01 = (v) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0)

/**
 * Coerce stored data into a valid option list summing to 1 — what a hand-edited
 * or older blueprint may not be. Unlocked options absorb any difference; if
 * there are none (or the locked ones alone exceed 1), everything is rescaled.
 */
export function normalizeOptions(raw) {
  let opts = Array.isArray(raw) && raw.length >= MIN_OPTIONS
    ? raw.map(o => ({ p: clamp01(Number(o?.p)), locked: o?.locked === true }))
    : defaultOptions(Array.isArray(raw) ? raw.length : MIN_OPTIONS)
  const lockedSum = sum(opts.filter(o => o.locked).map(o => o.p))
  const unlocked = opts.filter(o => !o.locked)
  if (unlocked.length > 0 && lockedSum <= 1) {
    const room = 1 - lockedSum
    const uSum = sum(unlocked.map(o => o.p))
    opts = opts.map(o => o.locked ? o : {
      ...o, p: uSum > 0 ? (o.p / uSum) * room : room / unlocked.length,
    })
  } else {
    const total = sum(opts.map(o => o.p))
    opts = opts.map(o => ({ ...o, p: total > 0 ? o.p / total : 1 / opts.length }))
  }
  return opts
}

/** Can option `i`'s probability be moved at all? */
export function canMove(opts, i) {
  if (!opts[i] || opts[i].locked) return false
  return opts.some((o, j) => j !== i && !o.locked)
}

/** The highest probability option `i` can take (1 minus the locked others). */
export function maxFor(opts, i) {
  return Math.max(0, 1 - sum(opts.filter((o, j) => j !== i && o.locked).map(o => o.p)))
}

/**
 * Set option `i` to `p` and rebalance the other unlocked options so the total
 * stays 1. A locked or immovable option returns the list unchanged.
 */
export function setProbability(opts, i, p) {
  if (!canMove(opts, i)) return opts
  const max = maxFor(opts, i)
  const value = Math.min(max, clamp01(p))
  const remainder = max - value
  const others = opts.map((o, j) => j).filter(j => j !== i && !opts[j].locked)
  const othersSum = sum(others.map(j => opts[j].p))
  return opts.map((o, j) => {
    if (j === i) return { ...o, p: value }
    if (o.locked) return o
    return { ...o, p: othersSum > 0 ? (o.p / othersSum) * remainder : remainder / others.length }
  })
}

export function setLocked(opts, i, locked) {
  return opts.map((o, j) => (j === i ? { ...o, locked: !!locked } : o))
}

/** Append an option at 0 — nothing else changes. */
export function addOption(opts) {
  return [...opts, { p: 0, locked: false }]
}

/** Remove option `i`; its share goes back to the unlocked options. */
export function removeOption(opts, i) {
  if (opts.length <= MIN_OPTIONS || !opts[i]) return opts
  return normalizeOptions(opts.filter((_, j) => j !== i))
}

/**
 * Weighted draw: the index whose cumulative probability first exceeds `r`
 * (a uniform [0, 1) sample). Zero-probability options are never picked. A
 * list that sums to nothing picks option 0.
 */
export function pickIndex(opts, r = Math.random()) {
  const total = sum(opts.map(o => Math.max(0, o.p)))
  if (!(total > 0)) return 0
  let acc = 0
  const target = r * total
  for (let i = 0; i < opts.length; i++) {
    acc += Math.max(0, opts[i].p)
    if (target < acc) return i
  }
  // r just under 1 with float residue: the last option that can be picked.
  for (let i = opts.length - 1; i >= 0; i--) if (opts[i].p > 0) return i
  return 0
}
